// ============================================================
// AnnouncementPage — 公告與注意事項管理（管理端）
// ============================================================

import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';

interface Announcement {
  id: string;
  type: 'ANNOUNCEMENT' | 'NOTICE';
  title: string;
  body: string;
  publishedAt: string;
  pinned?: number;
  expiresAt?: string;
}

export function AnnouncementPage() {
  const [items, setItems] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Partial<Announcement> | null>(null);
  const [includeExpired, setIncludeExpired] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get<{ announcements: Announcement[] }>(
        `/announcements?includeExpired=${includeExpired}`
      );
      setItems(r.announcements);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [includeExpired]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const save = async (a: Partial<Announcement>) => {
    if (!a.title || !a.body || !a.type) return alert('請填標題、內容、類型');
    await api.post('/announcements', a);
    setEditing(null);
    fetchData();
  };

  const remove = async (id: string) => {
    if (!confirm('確定刪除此則公告？')) return;
    await api.delete(`/announcements/${id}`);
    fetchData();
  };

  const notices = items.filter(i => i.type === 'NOTICE');
  const anns = items.filter(i => i.type === 'ANNOUNCEMENT');

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-morandi-800">公告與注意事項</h1>
        <div className="flex items-center gap-3">
          <label className="text-xs text-morandi-400 flex items-center gap-1 cursor-pointer">
            <input
              type="checkbox"
              checked={includeExpired}
              onChange={e => setIncludeExpired(e.target.checked)}
            />
            顯示過期
          </label>
          <button
            onClick={() => setEditing({ type: 'ANNOUNCEMENT', title: '', body: '' })}
            className="px-4 py-2 bg-earth-400 hover:bg-earth-500 text-white rounded-btn text-sm"
          >+ 新增公告</button>
          <button
            onClick={() => setEditing({ type: 'NOTICE', title: '', body: '', pinned: 50 })}
            className="px-4 py-2 bg-sage-400 hover:bg-sage-500 text-white rounded-btn text-sm"
          >+ 新增注意事項</button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-morandi-400">載入中...</div>
      ) : (
        <>
          {/* 注意事項（置頂） */}
          {notices.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-morandi-700 mb-2">📌 注意事項（常駐）</h2>
              <div className="space-y-2">
                {notices.map(a => (
                  <Card key={a.id} a={a} onEdit={() => setEditing(a)} onDelete={() => remove(a.id)} />
                ))}
              </div>
            </div>
          )}

          {/* 公告 */}
          {anns.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-morandi-700 mb-2">📢 公告</h2>
              <div className="space-y-2">
                {anns.map(a => (
                  <Card key={a.id} a={a} onEdit={() => setEditing(a)} onDelete={() => remove(a.id)} />
                ))}
              </div>
            </div>
          )}

          {items.length === 0 && (
            <div className="text-center py-10 text-morandi-300">還沒有任何公告</div>
          )}
        </>
      )}

      {editing && (
        <EditModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

function Card({ a, onEdit, onDelete }: {
  a: Announcement; onEdit: () => void; onDelete: () => void;
}) {
  const expired = a.expiresAt && new Date(a.expiresAt) < new Date();
  return (
    <div className={`card-morandi p-4 ${expired ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-morandi-700">{a.title}</span>
            {a.pinned != null && a.pinned > 0 && (
              <span className="badge bg-amber-100 text-amber-600 text-xs">置頂 {a.pinned}</span>
            )}
            {expired && <span className="badge bg-morandi-200 text-morandi-500 text-xs">已過期</span>}
          </div>
          <div className="text-sm text-morandi-500 whitespace-pre-wrap mt-1">{a.body}</div>
          <div className="text-xs text-morandi-300 mt-2">
            {new Date(a.publishedAt).toLocaleString('zh-TW', { hour12: false })}
            {a.expiresAt && ` · 到期 ${new Date(a.expiresAt).toLocaleDateString('zh-TW')}`}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <button onClick={onEdit} className="text-xs text-earth-500 hover:text-earth-700">編輯</button>
          <button onClick={onDelete} className="text-xs text-rose-400 hover:text-rose-600">刪除</button>
        </div>
      </div>
    </div>
  );
}

function EditModal({ initial, onClose, onSave }: {
  initial: Partial<Announcement>;
  onClose: () => void;
  onSave: (a: Partial<Announcement>) => void;
}) {
  const [form, setForm] = useState<Partial<Announcement>>(initial);
  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-morandi-800">
          {initial.id ? '編輯' : '新增'} {form.type === 'NOTICE' ? '注意事項' : '公告'}
        </h2>

        <div>
          <label className="text-sm text-morandi-500 block mb-1">類型</label>
          <div className="flex gap-2">
            <button
              onClick={() => setForm({ ...form, type: 'ANNOUNCEMENT' })}
              className={`flex-1 py-2 rounded-btn text-sm transition-colors
                ${form.type === 'ANNOUNCEMENT' ? 'bg-earth-400 text-white' : 'bg-morandi-100 text-morandi-500'}`}
            >📢 公告（有時效）</button>
            <button
              onClick={() => setForm({ ...form, type: 'NOTICE' })}
              className={`flex-1 py-2 rounded-btn text-sm transition-colors
                ${form.type === 'NOTICE' ? 'bg-sage-400 text-white' : 'bg-morandi-100 text-morandi-500'}`}
            >📌 注意事項（常駐）</button>
          </div>
        </div>

        <div>
          <label className="text-sm text-morandi-500 block mb-1">標題 *</label>
          <input
            type="text"
            value={form.title ?? ''}
            onChange={e => setForm({ ...form, title: e.target.value })}
            className="input-field"
            placeholder="例：五一勞動節連假排班提醒"
          />
        </div>

        <div>
          <label className="text-sm text-morandi-500 block mb-1">內容 *</label>
          <textarea
            value={form.body ?? ''}
            onChange={e => setForm({ ...form, body: e.target.value })}
            rows={6}
            className="input-field"
            placeholder="可換行輸入多段文字"
          />
        </div>

        {form.type === 'NOTICE' && (
          <div>
            <label className="text-sm text-morandi-500 block mb-1">置頂順序（數字越大越上方）</label>
            <input
              type="number"
              value={form.pinned ?? 50}
              onChange={e => setForm({ ...form, pinned: Number(e.target.value) })}
              className="input-field"
            />
          </div>
        )}

        {form.type === 'ANNOUNCEMENT' && (
          <div>
            <label className="text-sm text-morandi-500 block mb-1">到期日（選填）</label>
            <input
              type="date"
              value={form.expiresAt ? form.expiresAt.slice(0, 10) : ''}
              onChange={e => setForm({ ...form, expiresAt: e.target.value ? e.target.value + 'T23:59:59.000Z' : undefined })}
              className="input-field"
            />
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-morandi-500 hover:bg-morandi-100 rounded-btn"
          >取消</button>
          <button
            onClick={() => onSave(form)}
            className="px-4 py-2 text-sm bg-earth-400 hover:bg-earth-500 text-white rounded-btn"
          >儲存</button>
        </div>
      </div>
    </div>
  );
}
